/* eslint-disable @typescript-eslint/no-explicit-any */ // TODO: Remove this disable and fix all any types in this file
// Core types and utilities
import { ShopCore } from "@/abilities/shop/types/shop-core";

// GraphQL queries and mutations
import {
    addBasketItemMutation,
    addPriceRequest,
    basketForUserQuery,
    getArticleShopInfos,
} from "../../shop-core/graphql/shop-query";

// Store and composables
import { getWidgetItemFromInfoItem } from "../../shop-core/helper/get-widget-item-from-info-item";
import { useShopStatusStore } from "../../shop-core/store/shop-status.store";
import { useSapStore } from "../store/sap-shop-store";
import { useSapClient } from "./use-sap-client";

/**
 * Composable providing Sap shop integration functionality
 * @returns Collection of shop-related actions and utilities
 */
export function useSapShop(): ShopCore.IShopActions {
    // Initialize client and basket services
    const { getClient } = useSapClient();
    const sapStore = useSapStore();

    /**
     * Redirects user to the Sap shop with their basket
     * Sets up proper language and callback URL parameters
     */
    async function redirectToBasket(): Promise<void> {
        const store = useShopStatusStore();
        const config = store.sapConfig();

        if (!config) return;

        if (config?.shopUrl) {
            window.location.href = config.shopUrl;
        }
    }

    /**
     * Adds a part to the user's shopping basket
     * @param articleId - The ID of the article to add
     * @param amountSelected - The quantity to add
     */
    async function addPartToShopBasket(articleId: string, amountSelected: number): Promise<void> {
        const client = await getClient();
        const basketId = sapStore.cartId;

        if (client && basketId) {
            await client.mutate({
                mutation: addBasketItemMutation,
                variables: {
                    BasketId: basketId,
                    Amount: amountSelected,
                    ProductId: articleId,
                },
            });
        }
    }

    /**
     * Retrieves shop item information for a specific article
     * @param articleId - The ID of the article to get info for
     * @returns Promise with item information or null if not found
     */
    async function getShopItemInfoForArticle(articleId: string): Promise<ShopCore.WidgetItem | null> {
        const client = await getClient();
        if (!client) return null;

        const { data } = await client.query({
            query: getArticleShopInfos,
            variables: { articleId },
        });

        if (data.article === null) return null;

        return getWidgetItemFromInfoItem({
            price: data.article.price.price ?? null,
            showPriceOnRequest: data.article.showPriceOnRequest ?? false,
            amountAvailable: data.article.stock.stock ?? 0,
            stockStatus: data.article.stock.stockStatus ?? -1,
            currencySign: data.article.price.currency.sign ?? "",
        });
    }

    /**
     * Sends a price request for a specific article
     * @param articleId - The ID of the article to request price for
     */
    async function sendPriceRequestToShop(articleId: string): Promise<void> {
        const client = await getClient();
        if (!client) return;

        await client.mutate({
            mutation: addPriceRequest,
            variables: { articleId },
        });
    }

    /**
     * Retrieves the current user's basket data
     * @returns Promise with basket data or undefined if not found
     */
    async function getBasket(): Promise<any | undefined> {
        const client = await getClient();
        const basketId = sapStore.cartId;

        if (!client || !basketId) return undefined;

        try {
            const { data } = await client.query({
                query: basketForUserQuery,
                variables: { basketId },
                fetchPolicy: "network-only",
            });
            return data;
        } catch (error) {
            console.error("Failed to fetch basket:", error);
            return undefined;
        }
    }

    /**
     * Returns the Sap shop client instance
     * @returns Promise with the shop client
     */
    async function getShopClient() {
        return await getClient();
    }

    /**
     * Gets the basket ID for the current user
     * @returns The current user's basket ID
     */
    function getBasketIdForCurrentUser(): string | undefined {
        return sapStore.cartId;
    }

    // Return the public API
    return {
        // Core shopping functionality
        addPartToShopBasket,
        getShopItemInfoForArticle,
        sendPriceRequestToShop,

        // Basket operations
        getBasket,
        getBasketIdForCurrentUser,

        // Navigation and client access
        redirectToBasket,
        getShopClient,
    };
}
